import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Store, ShieldCheck, Star, MapPin, Clock, BadgeCheck, Phone, Calendar, Send, Globe, Navigation, Calculator, Crown, Sparkles, MessageCircle, Car, Truck, Bike, Tractor, Container, Search, Zap, ArrowUpDown, SlidersHorizontal, X, RotateCcw, Fuel, Cog, Gauge, Banknote, Plane, AlertTriangle, Anchor } from "lucide-react";
import { Logo } from "@/components/Aurora";
import ShowroomBanner from "@/components/ShowroomBanner";
import ShareButtons from "@/components/ShareButtons";
import { api, mediaUrl } from "@/lib/api";
import { crm } from "@/lib/crm";
import { useSuspended } from "@/lib/useSuspended";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import CarCard, { CarCardSkeleton } from "@/components/CarCard";
import { BoatTile } from "@/pages/Boats";
import { isCarpassCertified } from "@/lib/carMeta";
import { isTaxReady } from "@/lib/taxBelgium";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { extractId, sellerSlug } from "@/lib/slug";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SHOWROOM_CATS = [
  { key: "car", label: "Voitures", Icon: Car },
  { key: "van", label: "Utilitaires", Icon: Truck },
  { key: "moto", label: "Motos", Icon: Bike },
  { key: "agri", label: "Agricole", Icon: Tractor },
  { key: "truck", label: "Poids Lourds", Icon: Container },
];
const carCat = (c) => String(c.vehicle_type || c.category || "car").toLowerCase();

function CatTab({ active, onClick, label, count, Icon, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      aria-pressed={active}
      className={`group relative flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-300 ${active
        ? "force-dark bg-gradient-to-b from-[#1a3f66] to-[#0d1f34] text-white shadow-[0_10px_26px_-12px_rgba(0,0,0,0.85)] ring-1 ring-[#8FA3BC]/45"
        : "text-white/55 hover:bg-white/[0.05] hover:text-white"}`}>
      <Icon size={16} strokeWidth={1.75} className={active ? "text-[#C9D2DC]" : "text-white/40 group-hover:text-white/70"} />
      {label}
      <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums transition ${active ? "bg-[#C9D2DC]/15 text-[#C9D2DC]" : "bg-white/[0.06] text-white/40"}`}>{count}</span>
    </button>
  );
}

function FilterChip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[#8FA3BC]/30 bg-[#13304F]/40 px-2.5 py-1 text-[11px] font-bold text-white">
      {label}
      <button type="button" onClick={onClear} className="ml-0.5 rounded-full p-0.5 text-white/50 transition hover:bg-white/15 hover:text-white"><X size={11} /></button>
    </span>
  );
}

function FGroup({ label, Icon, children }) {
  return (
    <div className="border-t border-white/8 pt-3">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/45">
        {Icon && <Icon size={12} className="text-[#8FA3BC]" />} {label}
      </div>
      {children}
    </div>
  );
}

function SideRow({ active, onClick, Icon, label, count, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold transition-all duration-200 ${active
        ? "force-dark bg-gradient-to-b from-[#1a3f66] to-[#0d1f34] text-white ring-1 ring-[#8FA3BC]/45"
        : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
      <Icon size={15} className={active ? "text-[#C9D2DC]" : "text-white/40"} />
      <span className="flex-1 text-left">{label}</span>
      <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${active ? "bg-[#C9D2DC]/15 text-[#C9D2DC]" : "bg-white/[0.06] text-white/40"}`}>{count}</span>
    </button>
  );
}

function Stars({ value = 0, size = 15, onPick }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={onPick ? `review-star-${n}` : undefined} disabled={!onPick} onClick={() => onPick?.(n)} className={onPick ? "cursor-pointer" : "cursor-default"}>
          <Star size={size} className={n <= Math.round(value) ? "fill-[#C9D2DC] text-[#C9D2DC]" : "text-white/20"} />
        </button>
      ))}
    </span>
  );
}

export default function SellerProfile() {
  const { id: rawId } = useParams();
  const navigate = useNavigate();
  const [id, setId] = useState(() => extractId(rawId));
  const [slug, setSlug] = useState("");
  // Resolve the URL param (pure slug OR legacy id / "name--id") to the canonical
  // seller_id + slug, then rewrite the address bar to the clean /seller/<slug>.
  useEffect(() => {
    let alive = true;
    crm(`/seller/resolve?ref=${encodeURIComponent(rawId)}`)
      .then((r) => {
        if (!alive || !r) return;
        if (r.seller_id) setId(r.seller_id);
        setSlug(r.slug || "");
        if (r.slug && rawId !== r.slug) navigate(`/seller/${r.slug}`, { replace: true });
      })
      .catch(() => { if (alive) setId(extractId(rawId)); });
    return () => { alive = false; };
  }, [rawId, navigate]);
  const { t } = useT();
  const { user } = useAuth();
  const [seller, setSeller] = useState(null);
  const [cars, setCars] = useState([]);
  const [boats, setBoats] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [soldStats, setSoldStats] = useState({ recent: 0, month: 0, total: 0 });
  const [eliteFounder, setEliteFounder] = useState(false);
  const [vatBadge, setVatBadge] = useState(null);
  const [locFilter, setLocFilter] = useState("all");
  const [newFilter, setNewFilter] = useState(false);
  const [catFilter, setCatFilter] = useState("all");
  const [sortKey, setSortKey] = useState("relevance");
  const [search, setSearch] = useState("");
  const [evFilter, setEvFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const EMPTY_FLT = { make: "", model: "", priceMin: "", priceMax: "", yearMin: "", yearMax: "", kmMax: "", fuels: [], gearbox: "", states: [] };
  const [flt, setFlt] = useState(EMPTY_FLT);
  const setF = (k, v) => setFlt((p) => ({ ...p, [k]: v }));
  const toggleFuel = (f) => setFlt((p) => ({ ...p, fuels: p.fuels.includes(f) ? p.fuels.filter((x) => x !== f) : [...p.fuels, f] }));
  const toggleState = (s) => setFlt((p) => ({ ...p, states: p.states.includes(s) ? p.states.filter((x) => x !== s) : [...p.states, s] }));
  const norm = (s) => String(s || "").toLowerCase();
  const fuelLabel = (c) => { const f = norm(c.fuel); if (f.includes("électr") || f.includes("electr") || f.includes("elek")) return "Électrique"; if (f.includes("hybr")) return "Hybride"; if (f.includes("diesel") || f.includes("gasoil")) return "Diesel"; if (f.includes("essence") || f.includes("petrol") || f.includes("benz")) return "Essence"; if (f.includes("lpg") || f.includes("gpl")) return "LPG"; if (f.includes("cng")) return "CNG"; return c.fuel ? c.fuel : ""; };
  const gearOf = (c) => { const g = norm(c.transmission || c.gearbox); if (g.includes("auto")) return "Automatique"; if (g.includes("man")) return "Manuelle"; return ""; };
  const isEvCar = (c) => { const f = norm(c.fuel); return f.includes("electr") || f.includes("élec") || f.includes("hybrid"); };
  const evCount = cars.filter(isEvCar).length;
  const newCount = cars.filter((c) => c.new_vehicle).length;
  const STATE_DEFS = [
    { k: "export", label: "Export", Icon: Plane, count: cars.filter((c) => c.export).length },
    { k: "accident", label: "Accidenté", Icon: AlertTriangle, count: cars.filter((c) => c.accident).length },
    { k: "neuf", label: "Neuf", Icon: Sparkles, count: newCount },
    { k: "dna", label: "Certifié DNA", Icon: ShieldCheck, count: cars.filter((c) => c.dna_certified).length },
  ].filter((s) => s.count > 0);
  const stateMatch = (c, s) => s === "export" ? !!c.export : s === "accident" ? !!c.accident : s === "neuf" ? !!c.new_vehicle : s === "dna" ? !!c.dna_certified : true;
  const makesInStock = [...new Set(cars.map((c) => c.make).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const modelsInStock = [...new Set(cars.filter((c) => !flt.make || c.make === flt.make).map((c) => c.model).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const fuelsInStock = [...new Set(cars.map(fuelLabel).filter(Boolean))];
  const activeFilterCount = (flt.make ? 1 : 0) + (flt.model ? 1 : 0) + (flt.priceMin || flt.priceMax ? 1 : 0) + (flt.yearMin || flt.yearMax ? 1 : 0) + (flt.kmMax ? 1 : 0) + flt.fuels.length + (flt.gearbox ? 1 : 0) + flt.states.length;
  const evHaloActive = evFilter || flt.fuels.some((f) => f === "Électrique" || f === "Hybride");
  const catCounts = {};
  cars.forEach((c) => { const k = carCat(c); catCounts[k] = (catCounts[k] || 0) + 1; });
  const availableCats = SHOWROOM_CATS.filter((c) => catCounts[c.key] > 0);
  const [showroomsData, setShowroomsData] = useState({ showrooms: [], assignments: {} });
  const assignOf = (c) => showroomsData.assignments[String(c.id)] || "hq";
  const roomCounts = {};
  cars.forEach((c) => { const s = assignOf(c); roomCounts[s] = (roomCounts[s] || 0) + 1; });
  const filterRooms = (showroomsData.showrooms || []).filter((r) => roomCounts[r.id]);
  const showLocFilter = filterRooms.some((r) => !r.is_head_office);
  const visibleCars = (() => {
    let list = (locFilter === "all" ? cars : cars.filter((c) => assignOf(c) === locFilter))
      .filter((c) => (newFilter ? c.new_vehicle : true))
      .filter((c) => (catFilter === "all" ? true : carCat(c) === catFilter))
      .filter((c) => (evFilter ? isEvCar(c) : true));
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((c) => `${c.make || ""} ${c.model || ""} ${c.title || ""} ${c.version || ""}`.toLowerCase().includes(q));
    const num = (v) => Number(v) || 0;
    if (flt.make) list = list.filter((c) => c.make === flt.make);
    if (flt.model) list = list.filter((c) => c.model === flt.model);
    if (flt.priceMin) list = list.filter((c) => num(c.price) >= num(flt.priceMin));
    if (flt.priceMax) list = list.filter((c) => num(c.price) <= num(flt.priceMax));
    if (flt.yearMin) list = list.filter((c) => num(c.year) >= num(flt.yearMin));
    if (flt.yearMax) list = list.filter((c) => num(c.year) <= num(flt.yearMax));
    if (flt.kmMax) list = list.filter((c) => num(c.mileage) <= num(flt.kmMax));
    if (flt.fuels.length) list = list.filter((c) => flt.fuels.includes(fuelLabel(c)));
    if (flt.gearbox) list = list.filter((c) => gearOf(c) === flt.gearbox);
    if (flt.states.length) list = list.filter((c) => flt.states.every((s) => stateMatch(c, s)));
    const sorters = {
      price_asc: (a, b) => num(a.price) - num(b.price),
      price_desc: (a, b) => num(b.price) - num(a.price),
      year_desc: (a, b) => num(b.year) - num(a.year),
      km_asc: (a, b) => num(a.mileage) - num(b.mileage),
    };
    if (sorters[sortKey]) list = [...list].sort(sorters[sortKey]);
    return list;
  })();
  // Split cars from other vehicle types (motos, utilitaires, remorques…) so each
  // gets its own labelled section on the public showroom, exactly like boats.
  const OTHER_LABELS = { moto: "Motos", van: "Utilitaires", truck: "Camions", trailer: "Remorques", camper: "Camping-cars" };
  const carsOnly = visibleCars.filter((c) => carCat(c) === "car");
  const otherByType = {};
  visibleCars.forEach((c) => { const k = carCat(c); if (OTHER_LABELS[k]) (otherByType[k] = otherByType[k] || []).push(c); });
  const hasOthers = Object.keys(otherByType).length > 0;
  const invTabs = [
    ...(carsOnly.length ? [{ key: "car", label: "Voitures", count: carsOnly.length }] : []),
    ...Object.entries(otherByType).map(([k, v]) => ({ key: k, label: OTHER_LABELS[k], count: v.length })),
    ...(boats.length ? [{ key: "boat", label: "Bateaux", count: boats.length }] : []),
  ];
  const [invTab, setInvTab] = useState(null);
  const activeTab = (invTab && invTabs.some((t) => t.key === invTab)) ? invTab : (invTabs[0]?.key || "car");
  const [boatFlt, setBoatFlt] = useState({ type: "", lenMin: "", powMin: "" });
  const boatTypes = [...new Set(boats.map((b) => b.boat_type).filter(Boolean))];
  const filteredBoats = boats.filter((b) =>
    (!boatFlt.type || b.boat_type === boatFlt.type) &&
    (!boatFlt.lenMin || Number(b.length_m || 0) >= Number(boatFlt.lenMin)) &&
    (!boatFlt.powMin || Number(b.total_power_hp || 0) >= Number(boatFlt.powMin)));
  // Showroom infinite scroll: render the dealer's inventory in batches of 24.
  const [shown, setShown] = useState(24);
  const showroomSentinel = useRef(null);
  useEffect(() => { setShown(24); }, [cars.length, id, locFilter, newFilter, catFilter, evFilter, search, sortKey, flt]);
  useEffect(() => {
    if (shown >= visibleCars.length) return;
    const el = showroomSentinel.current;
    if (!el) return;
    const obs = new IntersectionObserver((e) => {
      if (e[0]?.isIntersecting) setShown((s) => Math.min(s + 24, visibleCars.length));
    }, { rootMargin: "600px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [shown, visibleCars.length]);

  const loadReviews = () => api(`/sellers/${id}/reviews`, { auth: false }).then((d) => setReviews(d || [])).catch(() => {});

  useEffect(() => {
    setLoading(true);
    setSeller(null);
    setCars([]);
    setBoats([]);
    (async () => {
      const [extSeller, ov] = await Promise.all([
        api(`/sellers/${id}`, { auth: false }).catch(() => null),
        crm(`/seller-override/${id}`).catch(() => null),
      ]);
      const owned = (await api(`/sellers/${id}/cars`, { auth: false }).catch(() => [])) || [];
      const imp = (await crm(`/sellers/${id}/imported-cars`).catch(() => ({}))) || {};
      const impCars = imp.cars || [];
      crm(`/sellers/${id}/boats`).then((b) => setBoats(Array.isArray(b) ? b : [])).catch(() => {});

      // Seller identity: external account > virtual (scraped) dealer, with the
      // admin/PRO override (name/slogan/banner/hours) overlaid on top.
      const base = extSeller || (imp.seller ? {
        name: imp.seller.name,
        company: imp.seller.name,
        logo: imp.seller.logo,
        address: imp.seller.location,
        website: imp.seller.url,
        phone: imp.seller.phone,
        merchant_verified: true,
        verified: true,
        listings_count: impCars.length,
      } : null);
      const s = base ? { ...base, ...(ov || {}) } : (ov || null);
      setSeller(s);

      const have = new Set(owned.map((c) => c.id));
      const extra = impCars.filter((c) => !have.has(c.id));
      const allCars = [...owned, ...extra];
      setCars(allCars);
      // Enrich with new-vehicle flags from our display layer (car_meta.override)
      // so the "Voitures neuves" tab + NEUF filter also cover OWNED (external)
      // listings, not only imported ones (imported-cars already carry the flag).
      (async () => {
        const ids = allCars.map((c) => String(c.id));
        const metaMap = {};
        for (let i = 0; i < ids.length; i += 100) {
          const chunk = ids.slice(i, i + 100);
          try {
            const res = await crm(`/car-meta/batch?ids=${chunk.map(encodeURIComponent).join(",")}`);
            Object.assign(metaMap, (res && res.meta) || {});
          } catch { /* best-effort */ }
        }
        const NV = ["list_price", "delivery_time", "availability", "warranty_months", "colors", "lease_promo"];
        let changed = false;
        const enriched = allCars.map((c) => {
          const ov = metaMap[String(c.id)]?.override;
          if (ov && ov.new_vehicle) {
            changed = true;
            const m = { ...c, new_vehicle: true, condition: "Neuf", mileage: 0 };
            for (const k of NV) if (ov[k] != null && ov[k] !== "") m[k] = ov[k];
            return m;
          }
          return c;
        });
        if (changed) setCars(enriched);
      })();
      crm(`/sellers/${id}/sold-stats`).then((st) => {
        const fromStatus = allCars.filter((c) => c.status === "sold").length;
        setSoldStats({ recent: st?.recent || 0, month: st?.month || 0, total: Math.max(st?.total || 0, fromStatus) });
      }).catch(() => {
        setSoldStats({ recent: 0, month: 0, total: allCars.filter((c) => c.status === "sold").length });
      });
      loadReviews();
      crm(`/sellers/${id}/badge`).then((b) => { setEliteFounder(!!b?.elite_founder); setVatBadge(b?.vat_valid ? { vat: b.vat, name: b.vat_name } : null); }).catch(() => {});
      crm(`/sellers/${id}/showrooms`).then((d) => setShowroomsData({ showrooms: d?.showrooms || [], assignments: d?.assignments || {} })).catch(() => {});
    })().finally(() => setLoading(false));
  }, [id]);

  const submitReview = async () => {
    if (!user) { toast.error(t("rev.loginNeeded")); return; }
    setPosting(true);
    try {
      await api(`/sellers/${id}/reviews`, { method: "POST", body: { rating, comment: comment.trim() } });
      toast.success(t("rev.thanks"));
      setComment("");
      await loadReviews();
    } catch (e) { toast.error(e.message || "Erreur"); }
    finally { setPosting(false); }
  };

  const name = seller?.company || seller?.name || "Vendeur";
  const suspendedIds = useSuspended();
  const isSuspended = suspendedIds.has(String(id));
  const reliability = seller?.reliability_score ?? seller?.seller_reliability;
  // On affiche le nombre RÉEL de véhicules dédupliqués actuellement en vitrine
  // (jamais le listings_count externe, qui compte les re-publications en double).
  const listingCount = cars.length;

  const badges = [];
  if (seller?.verified || seller?.seller_verified) badges.push({ icon: BadgeCheck, label: t("seller.verifiedId"), color: "#22C55E" });
  if (seller?.merchant_verified) badges.push({ icon: Store, label: "PRO " + t("common.verified"), color: "#C9D2DC" });
  if (vatBadge) badges.push({ icon: BadgeCheck, label: "TVA vérifiée · VIES", color: "#10B981", testid: "seller-vat-verified-badge" });
  if (seller?.fast_responder) badges.push({ icon: Clock, label: t("seller.fastResponder"), color: "#123A63" });
  const carpassAll = cars.length >= 1 && cars.every(isCarpassCertified);
  if (carpassAll) badges.push({ icon: ShieldCheck, label: t("seller.carpassAll"), color: "#10B981" });
  const taxReadyCount = cars.filter(isTaxReady).length;
  if (taxReadyCount > 0) badges.push({ icon: Calculator, label: `${taxReadyCount} ${t("seller.taxReady")}`, color: "#1E5288", testid: "seller-tax-ready-badge" });

  if (loading && !seller) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-10 lg:px-8" data-testid="seller-loading">
        <div className="h-52 animate-pulse rounded-2xl card-surface sm:h-64" />
        <div className="mt-6 flex items-center gap-4">
          <div className="h-16 w-16 animate-pulse rounded-2xl bg-white/10" />
          <div className="space-y-2">
            <div className="h-7 w-64 animate-pulse rounded bg-white/10" />
            <div className="h-4 w-40 animate-pulse rounded bg-white/10" />
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-white/5" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 pt-6 pb-44 sm:pt-10 lg:px-8 lg:pb-10">
      <Seo title={name} description={`Annonces et avis de ${name} sur Pactly Car.`} />

      {isSuspended ? (
        <div className="flex flex-col items-center rounded-2xl card-surface py-24 text-center" data-testid="seller-unavailable">
          <Store size={40} className="text-white/30" />
          <h1 className="mt-4 font-display text-xl font-bold text-white">Vendeur momentanément indisponible</h1>
          <p className="mt-1 max-w-md text-muted-foreground">Ce vendeur n'est pas actif sur Pactly Car pour le moment. Ses annonces ne sont pas visibles.</p>
        </div>
      ) : (
      <>
      {/* header — Navy & Silver institutional banner */}
      <div className="force-dark overflow-hidden rounded-3xl border border-white/10 bg-[#0F2540] shadow-[0_20px_60px_-24px_rgba(15,37,64,0.6)]">
        <div className={`relative overflow-hidden ${(seller?.banner_video || seller?.banner || seller?.establishment_photo) ? "h-48 sm:h-60" : "h-28 sm:h-44"}`}>
          {seller?.banner_video ? (
            <>
              <video data-testid="seller-banner-video" src={mediaUrl(seller.banner_video)} className="h-full w-full object-cover" style={{ objectPosition: `${seller?.banner_position_x ?? 50}% ${seller?.banner_position ?? 50}%` }} autoPlay muted loop playsInline />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2540] via-[#0F2540]/40 to-transparent" />
            </>
          ) : seller?.banner || seller?.establishment_photo ? (
            <>
              <img src={mediaUrl(seller.banner || seller.establishment_photo)} alt="" className="h-full w-full object-cover" style={{ objectPosition: `${seller?.banner_position_x ?? 50}% ${seller?.banner_position ?? 50}%` }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2540] via-[#0F2540]/40 to-transparent" />
            </>
          ) : (
            <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#0B1D33 0%,#12304f 55%,#0B1D33 100%)" }}>
              <div className="absolute inset-0" style={{ background: "radial-gradient(120% 90% at 15% 10%, rgba(155,168,183,0.16), transparent 55%)" }} />
              <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full" style={{ background: "radial-gradient(circle, rgba(155,168,183,0.12), transparent 60%)" }} />
              <div className="absolute inset-0 flex items-center justify-center px-6">
                <span data-testid="seller-default-banner-name" className="max-w-full truncate text-center font-display text-3xl font-black uppercase tracking-[0.18em] text-white/[0.07] sm:text-5xl">{name}</span>
              </div>
              <div className="absolute bottom-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-[#C9D2DC]/25 to-transparent" />
            </div>
          )}
          <div className="absolute right-4 top-4 z-10">
            <ShareButtons title={`${name} · Pactly Car`} url={`${BACKEND}/api/share/seller/${sellerSlug(name, id)}`} testid="seller-share" />
          </div>
        </div>
        <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-end">
          {/* Logo in a clean premium frame */}
          <span className="-mt-16 flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-2 shadow-xl ring-2 ring-[#C9D2DC] sm:-mt-20">
            {seller?.logo
              ? <img src={mediaUrl(seller.logo)} alt={name} className="h-full w-full rounded-full object-contain" />
              : <span className="font-display text-3xl font-bold text-[#0F2540]">{name.charAt(0)}</span>}
          </span>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#F5F8FB] sm:text-3xl">{name}</h1>
              {eliteFounder && (
                <span data-testid="seller-elite-founder-badge" className="plat-border inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#12304f] to-[#1b3a5c] px-3 py-1 text-xs font-bold">
                  <Crown size={13} className="text-[#e8edf3]" /> <span className="plat-chrome">Membre Élite Fondateur</span>
                </span>
              )}
              {(seller?.merchant_verified || seller?.verified) && (
                <span data-testid="seller-certified-badge" className="inline-flex items-center gap-1.5 rounded-full border border-[#C9D2DC]/40 bg-[#C9D2DC]/15 px-3 py-1 text-xs font-semibold text-[#C9D2DC]">
                  <BadgeCheck size={13} className="text-[#C9D2DC]" /> Partenaire Certifié
                </span>
              )}
            </div>
            {seller?.legal_subtext && <p data-testid="seller-legal-subtext" className="mt-0.5 text-sm font-light tracking-wide text-[#9BB0C6]">{seller.legal_subtext}</p>}
            {seller?.slogan && <p data-testid="seller-slogan" className="mt-1.5 text-sm font-medium text-[#C9D2DC]">{seller.slogan}</p>}
            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-[#C9D2DC]">
              {seller?.address && (
                <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#C9D2DC]" /> {seller.address}</span>
              )}
              {seller?.address && (
                <a data-testid="seller-directions" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(seller.address)}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-[#EAF0F6] transition hover:bg-white/10">
                  <Navigation size={12} className="text-[#C9D2DC]" /> Itinéraire
                </a>
              )}
              {seller?.opening_hours && <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#C9D2DC]" /> {seller.opening_hours}</span>}
              {seller?.website && <a href={seller.website.startsWith("http") ? seller.website : `https://${seller.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#C9D2DC] hover:text-[#EAF0F6] hover:underline"><Globe size={14} /> {t("seller.website")}</a>}
              {seller?.member_since && <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#C9D2DC]" /> {t("seller.memberSince")} {String(seller.member_since).slice(0, 4)}</span>}
              <span data-testid="seller-unique-count" className="text-[#C9D2DC]">· {listingCount} véhicules uniques{boats.length > 0 ? ` · ${boats.length} bateau${boats.length > 1 ? "x" : ""}` : ""}</span>
              <span data-testid="seller-nodup-note" className="inline-flex items-center gap-1.5 rounded-full border border-[#C9D2DC]/30 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-[#DDE4EC] backdrop-blur" title="Pactly regroupe automatiquement les re-publications d'une même voiture : votre stock reste net et vous n'êtes jamais facturé en double.">
                <ShieldCheck size={12} className="text-emerald-300" /> Doublons fusionnés automatiquement
              </span>
              {(soldStats.recent > 0 || soldStats.month > 0 || soldStats.total > 0) && (
                <span data-testid="seller-sold-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-300">
                  <BadgeCheck size={13} />
                  {soldStats.recent > 0
                    ? `${soldStats.recent} vendu${soldStats.recent > 1 ? "s" : ""} récemment`
                    : soldStats.month > 0
                      ? `${soldStats.month} vendu${soldStats.month > 1 ? "s" : ""} ce mois`
                      : `${soldStats.total} vendu${soldStats.total > 1 ? "s" : ""}`}
                </span>
              )}
              {seller?.review_count > 0 && <span className="flex items-center gap-1.5">· <Stars value={seller.review_avg} /> {seller.review_avg?.toFixed(1)} ({seller.review_count})</span>}
            </div>
          </div>
          {reliability != null && (
            <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-center">
              <div className="font-display text-2xl font-semibold text-[#C9D2DC]">{reliability}/100</div>
              <div className="text-xs text-[#B8C4D0]">{t("seller.trustScore")}</div>
            </div>
          )}
        </div>
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-white/10 px-6 py-4">
            {badges.map((b) => (
              <span key={b.label} data-testid={b.testid} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold" style={{ background: `${b.color}1E`, color: b.color }}>
                <b.icon size={13} /> {b.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
        {/* listings */}
        <div>
          {seller?.biz_description && (
            <div className="mb-6 rounded-2xl card-surface p-5" data-testid="seller-about">
              <h2 className="mb-2 font-display text-lg font-bold text-white">{t("seller.about")}</h2>
              <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{seller.biz_description}</p>
            </div>
          )}
          {seller?.gallery?.length > 0 && (
            <div className="mb-6" data-testid="seller-gallery">
              <h2 className="mb-3 font-display text-lg font-bold text-white">{t("seller.gallery")}</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {seller.gallery.map((g, i) => (
                  <div key={i} className="aspect-[4/3] overflow-hidden rounded-xl border border-white/10">
                    <img src={mediaUrl(g)} alt="" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <div className="mb-1.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#9BB0C9]">
                <span aria-hidden className="h-px w-6 bg-gradient-to-r from-[#C9D2DC] to-transparent" /> Inventaire
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-[26px]">{t("pro.myListings")}</h2>
            </div>
            <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-bold text-white/70 sm:inline-flex">
              <Store size={13} className="text-[#C9D2DC]" /> {cars.length} véhicule{cars.length > 1 ? "s" : ""}
              {boats.length > 0 && <span className="ml-1.5 flex items-center gap-1 border-l border-white/15 pl-1.5 text-emerald-300"><Anchor size={12} /> {boats.length} bateau{boats.length > 1 ? "x" : ""}</span>}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[250px_1fr] lg:items-start">
            {/* ── LEFT FILTER SIDEBAR (façon ArtoBike, premium) ───────────── */}
            <aside data-testid="showroom-sidebar" className={`${showFilters ? "block" : "hidden"} lg:block lg:sticky lg:top-24 lg:self-start`}>
              <div className="space-y-4 rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.01] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur showroom-filter-panel lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto showroom-sidebar-scroll" style={{ scrollbarWidth: "thin" }}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 font-display text-sm font-bold text-white"><SlidersHorizontal size={15} className="text-[#C9D2DC]" /> Filtres</span>
                  {activeFilterCount > 0 && (
                    <button data-testid="showroom-filters-reset" onClick={() => { setFlt(EMPTY_FLT); setSearch(""); }} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#9BB0C9] hover:text-white">
                      <RotateCcw size={11} /> Effacer
                    </button>
                  )}
                </div>

                <div className="relative">
                  <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input data-testid="showroom-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…"
                    className="showroom-search-input w-full rounded-xl border border-white/10 bg-white/5 py-2.5 pl-8 pr-3 text-sm font-medium text-white placeholder:text-white/35 focus:border-[#8FA3BC]/50 focus:outline-none" />
                </div>

                {availableCats.length > 1 && (
                  <FGroup label="Catégorie">
                    <div className="space-y-1" data-testid="showroom-category-tabs">
                      <SideRow active={catFilter === "all"} onClick={() => setCatFilter("all")} Icon={Store} label="Tous" count={cars.length} testid="showroom-cat-all" />
                      {availableCats.map((c) => (
                        <SideRow key={c.key} active={catFilter === c.key} onClick={() => setCatFilter(c.key)} Icon={c.Icon} label={c.label} count={catCounts[c.key]} testid={`showroom-cat-${c.key}`} />
                      ))}
                    </div>
                  </FGroup>
                )}

                {fuelsInStock.length > 0 && (
                  <FGroup label="Carburant" Icon={Fuel}>
                    <div className="flex flex-wrap gap-1.5">
                      {fuelsInStock.map((f) => {
                        const on = flt.fuels.includes(f);
                        const isEv = f === "Électrique" || f === "Hybride";
                        return (
                          <button key={f} type="button" data-testid={`flt-fuel-${f}`} onClick={() => toggleFuel(f)}
                            className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition ${on ? (isEv ? "text-[#0A1024]" : "force-dark bg-gradient-to-b from-[#1a3f66] to-[#0d1f34] text-white ring-1 ring-[#8FA3BC]/45") : "border border-white/10 text-white/60 hover:bg-white/5"}`}
                            style={on && isEv ? { background: "linear-gradient(90deg,#39FF6A,#8CFFB0)", boxShadow: "0 0 12px rgba(57,255,106,0.4)" } : undefined}>
                            {isEv && <Zap size={11} className="mr-1 inline" />}{f}
                          </button>
                        );
                      })}
                    </div>
                  </FGroup>
                )}

                {STATE_DEFS.length > 0 && (
                  <FGroup label="État & situation" Icon={BadgeCheck}>
                    <div className="flex flex-wrap gap-1.5">
                      {STATE_DEFS.map(({ k, label, Icon, count }) => {
                        const on = flt.states.includes(k);
                        const isExp = k === "export";
                        return (
                          <button key={k} type="button" data-testid={`flt-state-${k}`} onClick={() => toggleState(k)}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition ${on
                              ? (isExp ? "force-dark bg-gradient-to-r from-[#14243A] to-[#1E3A8A] text-white ring-1 ring-[#C9D2DC]/50" : "force-dark bg-gradient-to-b from-[#1a3f66] to-[#0d1f34] text-white ring-1 ring-[#8FA3BC]/45")
                              : "border border-white/10 text-white/60 hover:bg-white/5"}`}>
                            <Icon size={12} className={on ? (isExp ? "text-[#C9D2DC]" : "text-[#C9D2DC]") : "text-white/40"} />
                            {label}
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] tabular-nums ${on ? "bg-white/15 text-white" : "bg-white/[0.06] text-white/40"}`}>{count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </FGroup>
                )}

                <FGroup label="Marque & modèle">
                  <select data-testid="flt-make" value={flt.make} onChange={(e) => { setF("make", e.target.value); setF("model", ""); }}
                    className="showroom-sort-select w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs font-semibold text-white focus:border-[#8FA3BC]/50 focus:outline-none">
                    <option value="">Toutes les marques</option>
                    {makesInStock.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select data-testid="flt-model" value={flt.model} onChange={(e) => setF("model", e.target.value)} disabled={!flt.make}
                    className="showroom-sort-select mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs font-semibold text-white focus:border-[#8FA3BC]/50 focus:outline-none disabled:opacity-40">
                    <option value="">{flt.make ? "Tous les modèles" : "Choisir une marque"}</option>
                    {modelsInStock.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </FGroup>

                <FGroup label="Prix (€)" Icon={Banknote}>
                  <div className="flex items-center gap-1.5">
                    <input data-testid="flt-price-min" type="number" inputMode="numeric" value={flt.priceMin} onChange={(e) => setF("priceMin", e.target.value)} placeholder="Min" className="showroom-search-input w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-white placeholder:text-white/30 focus:border-[#8FA3BC]/50 focus:outline-none" />
                    <span className="text-white/25">–</span>
                    <input data-testid="flt-price-max" type="number" inputMode="numeric" value={flt.priceMax} onChange={(e) => setF("priceMax", e.target.value)} placeholder="Max" className="showroom-search-input w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-white placeholder:text-white/30 focus:border-[#8FA3BC]/50 focus:outline-none" />
                  </div>
                </FGroup>

                <FGroup label="Année" Icon={Calendar}>
                  <div className="flex items-center gap-1.5">
                    <input data-testid="flt-year-min" type="number" inputMode="numeric" value={flt.yearMin} onChange={(e) => setF("yearMin", e.target.value)} placeholder="Min" className="showroom-search-input w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-white placeholder:text-white/30 focus:border-[#8FA3BC]/50 focus:outline-none" />
                    <span className="text-white/25">–</span>
                    <input data-testid="flt-year-max" type="number" inputMode="numeric" value={flt.yearMax} onChange={(e) => setF("yearMax", e.target.value)} placeholder="Max" className="showroom-search-input w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-white placeholder:text-white/30 focus:border-[#8FA3BC]/50 focus:outline-none" />
                  </div>
                </FGroup>

                <FGroup label="Kilométrage max." Icon={Gauge}>
                  <input data-testid="flt-km-max" type="number" inputMode="numeric" value={flt.kmMax} onChange={(e) => setF("kmMax", e.target.value)} placeholder="Ex : 100 000 km" className="showroom-search-input w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-white placeholder:text-white/30 focus:border-[#8FA3BC]/50 focus:outline-none" />
                </FGroup>

                <FGroup label="Boîte de vitesses" Icon={Cog}>
                  <div className="flex gap-1.5">
                    {["Manuelle", "Automatique"].map((g) => (
                      <button key={g} type="button" data-testid={`flt-gear-${g}`} onClick={() => setF("gearbox", flt.gearbox === g ? "" : g)}
                        className={`flex-1 rounded-lg px-2 py-2 text-[11px] font-bold transition ${flt.gearbox === g ? "force-dark bg-gradient-to-b from-[#1a3f66] to-[#0d1f34] text-white ring-1 ring-[#8FA3BC]/45" : "border border-white/10 text-white/60 hover:bg-white/5"}`}>{g === "Automatique" ? "Auto" : "Manuelle"}</button>
                    ))}
                  </div>
                </FGroup>

                <div className="flex items-center justify-between border-t border-white/8 pt-3" data-testid="showroom-result-count">
                  <span className="text-xs font-bold text-white/70"><span className="font-display text-lg text-[#C9D2DC]">{visibleCars.length}</span> résultat{visibleCars.length > 1 ? "s" : ""}</span>
                  <button type="button" className="text-xs font-bold text-[#9BB0C9] hover:text-white lg:hidden" onClick={() => setShowFilters(false)}>Voir</button>
                </div>
              </div>
            </aside>

            {/* ── RIGHT : barre (tri) + puces + grille ────────────────────── */}
            <div className="min-w-0">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <button type="button" data-testid="showroom-filters-toggle" onClick={() => setShowFilters((v) => !v)}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition lg:hidden ${activeFilterCount ? "force-dark bg-gradient-to-b from-[#1a3f66] to-[#0d1f34] text-white ring-1 ring-[#8FA3BC]/45" : "border border-white/10 text-white/70"}`}>
                  <SlidersHorizontal size={13} /> Filtres{activeFilterCount > 0 && <span className="ml-0.5 rounded-full bg-[#39FF6A] px-1.5 py-0.5 text-[10px] text-[#0A1024]">{activeFilterCount}</span>}
                </button>
                <span className="hidden text-xs font-bold text-white/60 lg:inline">{visibleCars.length} résultat{visibleCars.length > 1 ? "s" : ""}</span>
                <div className="relative ml-auto">
                  <ArrowUpDown size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#C9D2DC]" />
                  <select data-testid="showroom-sort" value={sortKey} onChange={(e) => setSortKey(e.target.value)}
                    className="showroom-sort-select cursor-pointer appearance-none rounded-xl border border-white/10 bg-white/5 py-2 pl-8 pr-8 text-xs font-bold text-white focus:border-[#8FA3BC]/50 focus:outline-none">
                    <option value="relevance">Pertinence</option>
                    <option value="price_asc">Prix croissant</option>
                    <option value="price_desc">Prix décroissant</option>
                    <option value="year_desc">Année récente</option>
                    <option value="km_asc">Kilométrage ↓</option>
                  </select>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-1.5" data-testid="showroom-active-chips">
                  {flt.make && <FilterChip label={flt.make} onClear={() => { setF("make", ""); setF("model", ""); }} />}
                  {flt.model && <FilterChip label={flt.model} onClear={() => setF("model", "")} />}
                  {(flt.priceMin || flt.priceMax) && <FilterChip label={`${flt.priceMin || "0"}–${flt.priceMax || "∞"} €`} onClear={() => { setF("priceMin", ""); setF("priceMax", ""); }} />}
                  {(flt.yearMin || flt.yearMax) && <FilterChip label={`${flt.yearMin || "…"}–${flt.yearMax || "…"}`} onClear={() => { setF("yearMin", ""); setF("yearMax", ""); }} />}
                  {flt.kmMax && <FilterChip label={`≤ ${Number(flt.kmMax).toLocaleString("fr-BE")} km`} onClear={() => setF("kmMax", "")} />}
                  {flt.gearbox && <FilterChip label={flt.gearbox} onClear={() => setF("gearbox", "")} />}
                  {flt.fuels.map((f) => <FilterChip key={f} label={f} onClear={() => toggleFuel(f)} />)}
                  {flt.states.map((s) => <FilterChip key={s} label={(STATE_DEFS.find((d) => d.k === s) || {}).label || s} onClear={() => toggleState(s)} />)}
                </div>
              )}

              {newCount > 0 && (
                <div className="mb-4 flex flex-wrap gap-2" data-testid="showroom-new-filter">
                  <button data-testid="showroom-tab-all" onClick={() => setNewFilter(false)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition ${!newFilter ? "bg-gradient-to-b from-[#13304F] to-[#0F2540] text-white ring-1 ring-[#8FA3BC]/50" : "border border-white/10 text-white/60 hover:bg-white/5"}`}>
                    {t("nav.buy")} · Occasion &amp; neuf ({cars.length})
                  </button>
                  <button data-testid="showroom-tab-new" onClick={() => setNewFilter(true)}
                    className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition ${newFilter ? "bg-gradient-to-r from-[#1E5288] to-[#3b82f6] text-white ring-1 ring-[#7FB2E8]/50" : "border border-[#7FB2E8]/30 text-[#7FB2E8] hover:bg-[#3b82f6]/10"}`}>
                    <Sparkles size={12} /> Voitures neuves ({newCount})
                  </button>
                </div>
              )}
              {showLocFilter && (
                <div className="mb-4 flex flex-wrap gap-2" data-testid="showroom-city-filter">
                  <button data-testid="loc-filter-all" onClick={() => setLocFilter("all")}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition ${locFilter === "all" ? "bg-gradient-to-b from-[#13304F] to-[#0F2540] text-white ring-1 ring-[#8FA3BC]/50" : "border border-white/10 text-white/60 hover:bg-white/5"}`}>
                    Toutes les villes ({cars.length})
                  </button>
                  {filterRooms.map((r) => (
                    <button key={r.id} data-testid={`loc-filter-${r.id}`} onClick={() => setLocFilter(r.id)}
                      className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition ${locFilter === r.id ? "bg-gradient-to-b from-[#13304F] to-[#0F2540] text-white ring-1 ring-[#8FA3BC]/50" : "border border-white/10 text-white/60 hover:bg-white/5"}`}>
                      <MapPin size={12} className="text-[#AEBCCD]" /> {r.city || r.name} ({roomCounts[r.id]})
                    </button>
                  ))}
                </div>
              )}
              {loading ? <div className="text-muted-foreground">{t("common.loading")}</div>
                : invTabs.length === 0 ? (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] py-16 text-center" data-testid="showroom-empty">
                    <Search size={26} className="mx-auto mb-3 text-white/25" />
                    <p className="text-sm font-semibold text-white/70">Aucun véhicule ne correspond à ces filtres.</p>
                    <button onClick={() => { setFlt(EMPTY_FLT); setSearch(""); }} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white/70 hover:bg-white/5"><RotateCcw size={12} /> Réinitialiser les filtres</button>
                  </div>
                ) : (
                  <>
                    {/* Onglets d'inventaire par type (Voitures / Motos / Bateaux…) */}
                    {invTabs.length > 1 && (
                      <div className="mb-5 flex flex-wrap gap-2 border-b border-white/10 pb-3" data-testid="showroom-tabs">
                        {invTabs.map((tb) => {
                          const on = activeTab === tb.key;
                          const Icon = tb.key === "boat" ? Anchor : Car;
                          return (
                            <button key={tb.key} data-testid={`showroom-tab-${tb.key}`} onClick={() => setInvTab(tb.key)}
                              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition ${on ? "bg-gradient-to-b from-[#13304F] to-[#0F2540] text-white ring-1 ring-[#8FA3BC]/50" : "border border-white/10 text-white/60 hover:bg-white/5"}`}>
                              <Icon size={13} className={tb.key === "boat" ? "text-emerald-300" : "text-[#8FBBEA]"} /> {tb.label} <span className="text-white/40">({tb.count})</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* VOITURES */}
                    {activeTab === "car" && (
                      <>
                        <div className="grid gap-5 sm:grid-cols-2" data-testid="showroom-grid">
                          {carsOnly.slice(0, shown).map((c) => (
                            evHaloActive && isEvCar(c)
                              ? <div key={c.id} data-testid={`ev-halo-${c.id}`} className="ev-halo rounded-[20px]"><CarCard car={c} variant="cardoen" /></div>
                              : <CarCard key={c.id} car={c} variant="cardoen" />
                          ))}
                          {shown < carsOnly.length && Array.from({ length: 2 }).map((_, i) => <CarCardSkeleton key={`sk-${i}`} />)}
                        </div>
                        {shown < carsOnly.length && (
                          <div ref={showroomSentinel} className="mt-6 flex items-center justify-center py-4" data-testid="showroom-scroll-sentinel" aria-hidden>
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                          </div>
                        )}
                      </>
                    )}

                    {/* AUTRES TYPES (motos / utilitaires / camions / remorques / camping-cars) */}
                    {otherByType[activeTab] && (
                      <div className="grid gap-5 sm:grid-cols-2" data-testid={`showroom-type-${activeTab}`}>
                        {otherByType[activeTab].map((c) => <CarCard key={c.id} car={c} variant="cardoen" />)}
                      </div>
                    )}

                    {/* BATEAUX — avec filtres dédiés (type / longueur / puissance) */}
                    {activeTab === "boat" && (
                      <div data-testid="showroom-boats">
                        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="boat-filters">
                          <select data-testid="boat-filter-type" value={boatFlt.type} onChange={(e) => setBoatFlt((f) => ({ ...f, type: e.target.value }))}
                            className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none">
                            <option value="" className="bg-[#0F2540]">Tous les types</option>
                            {boatTypes.map((tp) => <option key={tp} value={tp} className="bg-[#0F2540]">{tp}</option>)}
                          </select>
                          <select data-testid="boat-filter-length" value={boatFlt.lenMin} onChange={(e) => setBoatFlt((f) => ({ ...f, lenMin: e.target.value }))}
                            className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none">
                            <option value="" className="bg-[#0F2540]">Longueur min.</option>
                            {[6, 8, 10, 12, 15, 20].map((m) => <option key={m} value={m} className="bg-[#0F2540]">≥ {m} m</option>)}
                          </select>
                          <select data-testid="boat-filter-power" value={boatFlt.powMin} onChange={(e) => setBoatFlt((f) => ({ ...f, powMin: e.target.value }))}
                            className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none">
                            <option value="" className="bg-[#0F2540]">Puissance min.</option>
                            {[50, 100, 150, 200, 300, 500].map((p) => <option key={p} value={p} className="bg-[#0F2540]">≥ {p} ch</option>)}
                          </select>
                          {(boatFlt.type || boatFlt.lenMin || boatFlt.powMin) && (
                            <button data-testid="boat-filter-reset" onClick={() => setBoatFlt({ type: "", lenMin: "", powMin: "" })}
                              className="inline-flex items-center gap-1 rounded-xl border border-white/15 px-3 py-2 text-xs font-bold text-white/70 hover:bg-white/5"><RotateCcw size={12} /> Réinitialiser</button>
                          )}
                          <span className="ml-auto text-xs font-bold text-white/60">{filteredBoats.length} bateau{filteredBoats.length > 1 ? "x" : ""}</span>
                        </div>
                        {filteredBoats.length === 0 ? (
                          <div className="rounded-2xl border border-white/10 bg-white/[0.02] py-12 text-center text-sm text-white/60">Aucun bateau ne correspond à ces filtres.</div>
                        ) : (
                          <div className="grid gap-5 sm:grid-cols-2">
                            {filteredBoats.map((b) => <BoatTile key={b.id} b={b} />)}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
            </div>
          </div>
        </div>

        {/* reviews */}
        <div>
          {(seller?.phone || seller?.whatsapp || seller?.address) && (
            <div data-testid="seller-contact-card" className="mb-6 hidden rounded-2xl card-surface p-5 lg:sticky lg:top-24 lg:block">
              <h2 className="mb-3 font-display text-lg font-bold text-white">Contacter {name}</h2>
              <div className="space-y-2.5">
                {seller?.phone && (
                  <a data-testid="seller-contact-call" href={`tel:${seller.phone}`} className="flex items-center gap-2.5 rounded-xl bg-white/5 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/10">
                    <Phone size={16} className="text-[#8FBBEA]" /> {seller.phone}
                  </a>
                )}
                {(seller?.whatsapp || seller?.phone) && (
                  <a data-testid="seller-contact-whatsapp" href={`https://wa.me/${String(seller.whatsapp || seller.phone).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 rounded-xl bg-[#25D366]/15 px-4 py-3 text-sm font-bold text-[#25D366] transition hover:bg-[#25D366]/25">
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                )}
                {seller?.address && (
                  <a data-testid="seller-contact-directions" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(seller.address)}`} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 rounded-xl bg-white/5 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/10">
                    <Navigation size={16} className="text-[#8FBBEA]" /> Itinéraire
                  </a>
                )}
              </div>
            </div>
          )}
          <h2 className="mb-4 font-display text-xl font-bold text-white">{t("rev.title")}</h2>
          <div className="rounded-2xl card-surface p-5" data-testid="review-form">
            <span className="text-sm font-semibold text-white/80">{t("rev.your")}</span>
            <div className="mt-1"><Stars value={rating} size={22} onPick={setRating} /></div>
            <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
              placeholder={t("rev.comment")} className="mt-3 bg-white/5 text-white" />
            <Button data-testid="review-submit" onClick={submitReview} disabled={posting} className="mt-3 w-full gradient-brand font-bold text-white">
              <Send size={15} className="mr-2" /> {t("rev.submit")}
            </Button>
          </div>

          <div className="mt-4 space-y-3" data-testid="reviews-list">
            {reviews.length === 0 ? (
              <p className="rounded-2xl card-surface p-6 text-center text-sm text-muted-foreground">{t("rev.empty")}</p>
            ) : reviews.map((r) => (
              <div key={r.id} className="rounded-2xl card-surface p-4">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-sm font-bold text-white">{(r.reviewer_name || "?").charAt(0)}</span>
                    <span className="text-sm font-semibold text-white">{r.reviewer_name || "Client"}</span>
                  </span>
                  <Stars value={r.rating} size={13} />
                </div>
                {r.comment && <p className="mt-2 text-sm text-white/80">{r.comment}</p>}
                <div className="mt-1 text-xs text-muted-foreground">{String(r.created_at || "").slice(0, 10)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile sticky contact bar — quick actions for buyers on phones */}
      {(seller?.phone || seller?.whatsapp || seller?.address) && (
        <div data-testid="seller-mobile-cta" className="fixed inset-x-0 bottom-20 z-40 border-t border-white/10 bg-[#0B1D33]/95 px-3 py-2.5 backdrop-blur-md lg:hidden">
          <div className="mx-auto flex max-w-lg items-center gap-2">
            {seller?.phone && (
              <a data-testid="seller-cta-call" href={`tel:${seller.phone}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-white/10 py-2.5 text-sm font-bold text-white transition active:scale-95">
                <Phone size={16} /> Appeler
              </a>
            )}
            {(seller?.whatsapp || seller?.phone) && (
              <a data-testid="seller-cta-whatsapp" href={`https://wa.me/${String(seller.whatsapp || seller.phone).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#25D366] py-2.5 text-sm font-bold text-white transition active:scale-95">
                <MessageCircle size={16} /> WhatsApp
              </a>
            )}
            {seller?.address && (
              <a data-testid="seller-cta-directions" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(seller.address)}`} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-white/10 py-2.5 text-sm font-bold text-white transition active:scale-95">
                <Navigation size={16} /> Itinéraire
              </a>
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
